import { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  REQUEST_PASSWORD_RESET,
  VERIFY_PASSWORD_RESET_CODE,
  RESET_PASSWORD,
} from '../queries';
import { validatePassword } from '../utils';

export type PasswordResetStep = 'request' | 'verify' | 'reset' | 'success';

interface UsePasswordResetParams {
  onSuccess?: () => void;
  onBack?: () => void;
}

/**
 * Custom hook for password reset flow
 *
 * Three-step flow:
 * 1. Request: User enters email or phone, receives verification code
 * 2. Verify: User enters code, receives reset token
 * 3. Reset: User enters new password, password is updated
 */
export const usePasswordReset = ({
  onSuccess,
  onBack,
}: UsePasswordResetParams = {}) => {
  // Step state
  const [step, setStep] = useState<PasswordResetStep>('request');

  // Form state
  const [identifier, setIdentifier] = useState('');
  const [identifierType, setIdentifierType] = useState<'email' | 'phone'>('email');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [error, setError] = useState('');

  // GraphQL mutations
  const [requestPasswordReset, { loading: requestingReset }] = useMutation(REQUEST_PASSWORD_RESET);
  const [verifyPasswordResetCode, { loading: verifyingCode }] = useMutation(VERIFY_PASSWORD_RESET_CODE);
  const [resetPassword, { loading: resettingPassword }] = useMutation(RESET_PASSWORD);

  // Step 1: Request password reset code
  const handleRequestReset = async () => {
    setError('');

    if (!identifier) {
      setError(identifierType === 'email' ? 'Please enter your email' : 'Please enter your phone number');
      return;
    }

    try {
      console.log('🔐 [RESET-1] Requesting password reset for:', identifierType, identifier);

      const input: any = {};
      if (identifierType === 'email') {
        input.email = identifier.toLowerCase();
      } else {
        input.phoneNumber = identifier;
      }

      const { data } = await requestPasswordReset({
        variables: { input },
      });

      if (data?.requestPasswordReset) {
        console.log('✅ [RESET-2] Reset code sent');
        setStep('verify');
      } else {
        // Don't reveal if account exists
        console.log('✅ [RESET-2] Reset code sent (or account not found)');
        setStep('verify');
      }
    } catch (err: any) {
      console.error('❌ [RESET-ERROR]', err);
      // Don't reveal specific errors for security
      setStep('verify'); // Still move to verify step
    }
  };

  // Step 2: Verify the code
  const handleVerifyCode = async () => {
    setError('');

    if (!code || code.length !== 4) {
      setError('Please enter the 4-digit code');
      return;
    }

    try {
      console.log('🔐 [VERIFY-1] Verifying code for:', identifierType, identifier);

      const input: any = { code };
      if (identifierType === 'email') {
        input.email = identifier.toLowerCase();
      } else {
        input.phoneNumber = identifier;
      }

      const { data } = await verifyPasswordResetCode({
        variables: { input },
      });

      if (data?.verifyPasswordResetCode) {
        console.log('✅ [VERIFY-2] Code verified, got reset token');
        setResetToken(data.verifyPasswordResetCode);
        setStep('reset');
      } else {
        setError('Invalid or expired code');
      }
    } catch (err: any) {
      console.error('❌ [VERIFY-ERROR]', err);
      setError(err.message || 'Invalid or expired code');
    }
  };

  // Step 3: Reset the password
  const handleResetPassword = async () => {
    setError('');

    // Validate password
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!resetToken) {
      setError('Reset token not found. Please start over.');
      return;
    }

    try {
      console.log('🔐 [RESET-PWD-1] Resetting password');

      const { data } = await resetPassword({
        variables: {
          input: {
            resetToken,
            newPassword,
          },
        },
      });

      if (data?.resetPassword) {
        console.log('✅ [RESET-PWD-2] Password reset successful');
        setStep('success');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } catch (err: any) {
      console.error('❌ [RESET-PWD-ERROR]', err);
      setError(err.message || 'Failed to reset password');
    }
  };

  // Reset state and go back
  const handleBack = () => {
    setStep('request');
    setIdentifier('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setResetToken(null);
    setError('');
    if (onBack) {
      onBack();
    }
  };

  // Resend code
  const handleResendCode = async () => {
    setCode('');
    setError('');
    await handleRequestReset();
  };

  return {
    // State
    step,
    identifier,
    identifierType,
    code,
    newPassword,
    confirmPassword,
    error,
    loading: requestingReset || verifyingCode || resettingPassword,

    // Setters
    setStep,
    setIdentifier,
    setIdentifierType,
    setCode,
    setNewPassword,
    setConfirmPassword,
    setError,

    // Actions
    handleRequestReset,
    handleVerifyCode,
    handleResetPassword,
    handleResendCode,
    handleBack,
  };
};
